import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import useDebounce from '../../../hooks/useDebounce';
import { createQueryString } from '../../../lib/utils';
import { getConfigurations } from '../../../modules/config';
import { getTestCasesNotInSuite } from '../../../modules/testsuites';
import SearchBar from '../../../ui/primitives/SearchBar';
import TableComponent from '../../../ui/data/table/TableComponent';
import { testCasestableHeader } from '../test-case/testCaseTableHead';
import CustomSelect from '../../../ui/primitives/CustomSelect';

const AddTestCaseInSuite = ({ selectedSuite, selected, setSelected, technologyLayerOptions }) => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('desc');
  const [searchValue, setSearchValue] = useState('');
  const [testCases, setTestCases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const perPage = 20;
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const { projectId } = useParams();
  const { addNotification } = useNotification();
  const [statusOptions, setStatusOptions] = useState([]);
  const [filters, setFilters] = useState({
    status: 'All',
    technologyLayer: 'All',
  });

  const fetchTestCases = async (
    currentPage = 1,
    searchValue = '',
    queryString = ''
  ) => {
    setLoading(true);
    try {
      const response = await getTestCasesNotInSuite(
        selectedSuite?.id,
        projectId,
        currentPage,
        perPage,
        searchValue,
        queryString
      );
      setTestCases(response?.testcases || []);
      setTotalCount(response?.total || 0);
    } catch (error) {
      const errorMessage = error?.message || 'Failed to fetch test cases.';
      addNotification('danger', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const queryString = createQueryString(filters);
    fetchTestCases(page, debouncedSearchQuery, queryString);
  }, [page, debouncedSearchQuery, filters]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const fetchStatuses = async () => {
    try {
      const response = await getConfigurations('testcasestatus', projectId);
      if (response) {
        setStatusOptions(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const technologyLayerSelectOptions = [
    { id: 'All', name: 'All' },
    ...technologyLayerOptions,
  ];

  const statusSelectOptions = [
    { id: 'All', name: 'All' },
    ...statusOptions.filter((s) => s.name !== 'Archived'),
  ];

  return (
    <>
      <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <SearchBar
            placeholder="Search test case(s) to add"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            clearSearch={() => setSearchValue('')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomSelect
            id="technologyLayer"
            label="Technology Layer"
            value={filters.technologyLayer}
            onChange={(value) => handleFilterChange('technologyLayer', value)}
            options={technologyLayerSelectOptions}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomSelect
            id="status"
            label="Status"
            value={filters.status}
            onChange={(value) => handleFilterChange('status', value)}
            options={statusSelectOptions}
          />
        </Grid>
      </Grid>

      <Box>
        <TableComponent
          data={testCases || []}
          headerTabs={testCasestableHeader}
          showCheckbox
          page={page}
          setPage={setPage}
          perPage={perPage}
          order={order}
          setOrder={setOrder}
          defaultSort="created_at"
          selected={selected}
          isLoading={loading}
          striped
          setSelected={setSelected}
          totalCount={totalCount}
          showPages
          handlePageChange={handlePageChange}
          tablekey="test cases"
        />
      </Box>
    </>
  );
};

export default AddTestCaseInSuite;